<template>
  <div
    class="tw:rounded-xl tw:flex tw:flex-col tw:justify-between tw:p-4 tw:text-gray-800 tw:w-full tw:items-center tw:gap-y-6"
    :class="getClass"
    @click="selectCard"
  >
    <div class="tw:text-base tw:w-full tw:flex tw:items-center tw:text-gray-800">
      <div
        v-if="elementId === 'bookingDate'"
        class="tw:bg-decorative-lightblue tw:p-3 tw:h-[48px] tw:w-[48px] tw:flex tw:items-center tw:justify-center tw:mr-2 tw:rounded-full"
      >
        <RcBookingsIcon class="tw:size-[18px]" />
      </div>
      <div
        v-if="elementId === 'checkout'"
        class="tw:bg-decorative-lightblue tw:p-3 tw:flex tw:items-center tw:justify-center tw:mr-2 tw:rounded-full"
      >
        <RcCheckoutIcon class="tw:size-6" />
      </div>
      <div
        v-if="elementId === 'checkin'"
        class="tw:bg-decorative-lightblue tw:p-3 tw:flex tw:items-center tw:justify-center tw:mr-2 tw:rounded-full"
      >
        <RcCheckinIcon class="tw:size-6" />
      </div>
      <div
        v-if="elementId === 'commissions'"
        class="tw:bg-decorative-lightblue tw:p-3 tw:flex tw:items-center tw:justify-center tw:mr-2 tw:rounded-full"
      >
        <RcCommissionsIcon class="tw:size-6" />
      </div>

      <div class="tw:mr-1">
        <p class="tw:text-left tw:font-bold tw:text-xl">{{ $t(label) }}</p>
        <div v-if="subtitle" class="tw:text-xs">
          <p>
            {{ subtitleText ? $t(subtitleText) : '' }}
            <span>{{ $t(timestampsMap[selectedBookingPeriod].key, timestampsMap[selectedBookingPeriod].params) }}</span>
          </p>
        </div>
      </div>

      <UiTooltip theme="customdarkwithpadding" tooltip-position="top" :label="getTooltip" class="tw:cursor-pointer">
        <InformationOutline class="tw:fill-none tw:h-[19px] tw:w-[19px] tw:stroke-gray-500 tw:ml-1" />
      </UiTooltip>
    </div>
    <div class="tw:flex tw:w-full tw:justify-start tw:items-center tw:flex-wrap">
      <p class="tw:text-[28px] tw:mr-3">{{ formatNumberValue }}</p>
      <div
        class="tw:gap-x-1 tw:font-bold tw:bg-white tw:flex tw:justify-center tw:items-center tw:border tw:border-gray-300 tw:rounded-md tw:py-1 tw:px-2"
      >
        <ArrowUpRight v-if="isPositive" class="tw:size-[11px] tw:fill-[#0A1121]" />
        <ArrowDownRight v-else class="tw:size-[11px] tw:fill-[#0A1121]" />
        {{
          hasNoData || hasEmptyTable || (selectedBookingPeriod.includes('next') && elementId === 'bookingDate')
            ? '-%'
            : formatPercentage(progression, 0, 0)
        }}
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'
import RcCommissionsIcon from '@/components/icons/RcCommissionsIcon.svg?component'
import RcBookingsIcon from '@/components/icons/RcBookingsIcon.svg?component'
import RcCheckoutIcon from '@/components/icons/RcCheckoutIcon.svg?component'
import RcCheckinIcon from '@/components/icons/RcCheckinIcon.svg?component'
import ArrowUpRight from '@/components/icons/ArrowUpRight.svg?component'
import ArrowDownRight from '@/components/icons/ArrowDownRight.svg?component'

import { useRateConnectPerformance } from './useRateConnectPerformance'
import { useFormatting } from '@/composables/useFormatting'

const { t } = useI18n()
const props = defineProps({
  label: {
    type: String,
    required: true,
    default: '',
  },
  elementId: {
    type: String,
    required: false,
    default: '',
  },
  value: {
    type: Number,
    required: false,
    default: null,
  },
  number: {
    type: Number,
    required: false,
    default: null,
  },
  index: {
    type: Number,
    required: true,
    default: 0,
  },
  subtitle: {
    type: Boolean,
    required: false,
  },
  subtitleText: {
    type: String,
    required: false,
    default: '',
  },
  progression: Number,
  hasNoData: Boolean,
  hasEmptyTable: Boolean,
})

const { selectedBookingPeriod, selectedDateType, bookingSortColumn } = useRateConnectPerformance()
const selectCard = () => {
  if (props.elementId === 'bookingDate' && !selectedBookingPeriod.value.includes('next')) {
    selectedDateType.value = 'bookingDate'
    bookingSortColumn.value = 'bookingDate'
  }
  if (props.elementId === 'checkin') {
    selectedDateType.value = 'arrivalDate'
    bookingSortColumn.value = 'arrivalDate'
  }
  if (props.elementId === 'checkout') {
    selectedDateType.value = 'departureDate'
    bookingSortColumn.value = 'departureDate'
  }
  if (props.elementId === 'commissions') {
    selectedDateType.value = 'departureDate'
    bookingSortColumn.value = 'bookingValue.commissionCost'
  }
}

const getMonth = (type, count = 0, position = 'current') => {
  const today = new Date()
  const currentMonth = today.getMonth() + 1

  let month

  if (type === 'current') {
    month = currentMonth
  } else if (type === 'last') {
    if (position === 'end') {
      month = currentMonth - 1
    } else if (position === 'start') {
      month = currentMonth - 1 - (count - 1)
    }
  } else if (type === 'next') {
    if (position === 'start') {
      month = currentMonth + 1
    } else if (position === 'end') {
      month = currentMonth + count
    }
  }

  while (month <= 0) month += 12
  while (month > 12) month -= 12

  return `month_${month}`
}

const timestampsMap = computed(() => ({
  last_30_days: {
    key: 'rc_booking_filter_last_30_days',
    params: {},
  },
  last_90_days: {
    key: 'rc_booking_filter_last_90_days',
    params: {},
  },
  next_30_days: {
    key: 'rc_booking_filter_next_30_days',
    params: {},
  },
  next_90_days: {
    key: 'rc_booking_filter_next_90_days',
    params: {},
  },
  current_month: {
    key: 'rc_booking_filter_current_month',
    params: {
      Month: t(getMonth('current')),
    },
  },
  last_1_month: {
    key: 'rc_booking_filter_last_month',
    params: {
      Month: t(getMonth('last', 1, 'start')),
    },
  },
  last_3_months: {
    key: 'rc_booking_filter_last_3_months',
    params: {
      MonthStart: t(getMonth('last', 3, 'start')),
      MonthEnd: t(getMonth('last', 3, 'end')),
    },
  },
  last_6_months: {
    key: 'rc_booking_filter_last_6_months',
    params: {
      MonthStart: t(getMonth('last', 6, 'start')),
      MonthEnd: t(getMonth('last', 6, 'end')),
    },
  },
  last_12_months: {
    key: 'rc_booking_filter_last_12_months',
    params: {
      MonthStart: t(getMonth('last', 12, 'start')),
      MonthEnd: t(getMonth('last', 12, 'end')),
    },
  },
  next_1_month: {
    key: 'rc_booking_filter_next_month',
    params: {
      Month: t(getMonth('next', 1, 'start')),
    },
  },
  next_3_months: {
    key: 'rc_booking_filter_next_3_months',
    params: {
      MonthStart: t(getMonth('next', 3, 'start')),
      MonthEnd: t(getMonth('next', 3, 'end')),
    },
  },
  next_6_months: {
    key: 'rc_booking_filter_next_6_months',
    params: {
      MonthStart: t(getMonth('next', 6, 'start')),
      MonthEnd: t(getMonth('next', 6, 'end')),
    },
  },
}))

const tooltipTexts = {
  bookingDate: 'rc_def_bookings',
  checkin: 'rc_checkins_tooltip',
  checkout: 'rc_checkouts_tooltip',
  commissions: 'rc_commission_tooltip',
}
const { formatCurrency, formatPercentage, formatNumber } = useFormatting()
const getTooltip = computed(() => t(tooltipTexts[props.elementId]))
const getNumber = computed(() => props.number ?? 0)
const isPositive = computed(() => props.progression >= 0)
const formatNumberValue = computed(() => {
  if (
    props.hasEmptyTable ||
    props.hasNoData ||
    (props.elementId === 'bookingDate' && selectedBookingPeriod.value.includes('next'))
  )
    return '-'

  if (props.elementId === 'commissions') {
    return formatCurrency(getNumber.value)
  }

  return formatNumber(getNumber.value, 0, 2)
})

const isSelected = computed(() => {
  if (selectedDateType.value === 'bookingDate' && props.elementId === 'bookingDate') return true
  if (selectedDateType.value === 'arrivalDate' && props.elementId === 'checkin') return true
  if (
    selectedDateType.value === 'departureDate' &&
    props.elementId === 'checkout' &&
    bookingSortColumn.value !== 'bookingValue.commissionCost'
  )
    return true

  if (
    selectedDateType.value === 'departureDate' &&
    props.elementId === 'commissions' &&
    bookingSortColumn.value === 'bookingValue.commissionCost'
  )
    return true
  else return false
})
const getClass = computed(() => {
  const classes = []
  if (selectedBookingPeriod.value.includes('next') && props.elementId === 'bookingDate') {
    classes.push('tw:hover:cursor-default tw:grayscale tw:bg-gray-100')
  } else {
    classes.push('tw:hover:cursor-pointer tw:hover:border-blue-700 ')
  }
  if (isSelected.value) {
    if (props.index === 0) {
      classes.push('tw:bg-blue-200 tw:border-blue-700 tw:border tw:lg:ml-0 ')
    } else if (props.index === 3) {
      classes.push('tw:bg-blue-200 tw:border-blue-700 tw:border tw:lg:mr-0 ')
    } else {
      classes.push('tw:bg-blue-200 tw:border-blue-700 tw:border')
    }
  } else {
    if (props.index === 0) {
      classes.push(' tw:border-gray-300 tw:border tw:lg:ml-0')
    } else if (props.index === 3) {
      classes.push(' tw:border-gray-300 tw:border tw:lg:mr-0 ')
    } else {
      classes.push(' tw:border-gray-300 tw:border')
    }
  }
  return classes.join(' ')
})
</script>
